import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "@/lib/router-compat";
import { useRouterState } from "@tanstack/react-router";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOBreadcrumb from "@/components/SEOBreadcrumb";
import GatedContent from "@/components/insights/GatedContent";
import PremiumAssetBox from "@/components/insights/PremiumAssetBox";
import ArticleContentRenderer from "@/components/insights/ArticleContentRenderer";
import ArticleTableOfContents from "@/components/insights/ArticleTableOfContents";
import RelatedArticles from "@/components/insights/RelatedArticles";
import ArticleCaseCta from "@/components/insights/ArticleCaseCta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, ArrowLeft, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import SEO from "@/components/SEO";
import { isCorporateArticle } from "@/lib/insightsAudience";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { usePlanGate } from "@/hooks/usePlanUi";
import { Skeleton } from "@/components/ui/skeleton";

type FullArticle = {
  id: string;
  slug: string;
  slug_es: string;
  slug_en: string;
  article_type: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  content_html_es: string;
  content_html_en: string;
  cover_image_url: string | null;
  cover_image_alt: string;
  featured_image_alt_es: string;
  featured_image_alt_en: string;
  featured_image_title: string;
  keywords: any;
  seo_title_es: string;
  seo_title_en: string;
  seo_description_es: string;
  seo_description_en: string;
  premium_assets: any[];
  published_at: string;
  updated_at: string;
  category_id: string | null;
};

type Category = {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
  color: string;
};

/* ── Helpers ── */
const AUTHOR_NAME = "Retail Lemon Insights";
const AUTHOR_URL = "https://retaillemon.com";

function estimateReadingTime(html: string): number {
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const words = text.split(" ").length;
  return Math.max(1, Math.ceil(words / 220));
}

function injectHeadingIds(html: string): string {
  let counter = 0;
  const demoted = html
    .replace(/<h1(\s[^>]*)?>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>");
  return demoted.replace(/<(h[23])([^>]*)>/gi, (_, tag, attrs) => {
    const id = `heading-${counter++}`;
    return `<${tag}${attrs} id="${id}">`;
  });
}

const InsightArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // URL is the source of truth for language: any /en/... path forces English
  const isEnglishUrl = pathname.startsWith("/en/");
  const isGuideUrl = pathname.includes("/guias/") || pathname.includes("/guides/");

  // El loader de la ruta ya trae el artículo en SSR: lo usamos como estado
  // inicial para que el HTML servido lleve H1 y cuerpo reales (sin esto Google
  // veía el esqueleto de carga y lo marcaba como soft 404).
  const ssr = useRouterState({
    select: (s) =>
      (s.matches[s.matches.length - 1]?.loaderData as
        | { article?: unknown; category?: unknown }
        | undefined) ?? undefined,
  });
  const ssrArticle = (ssr?.article as FullArticle | undefined) ?? null;
  const [article, setArticle] = useState<FullArticle | null>(
    ssrArticle ? ({ ...ssrArticle, premium_assets: [] } as FullArticle) : null,
  );
  const [category, setCategory] = useState<Category | null>(
    (ssr?.category as Category | undefined) ?? null,
  );
  const [loading, setLoading] = useState(!ssrArticle);

  // Lectura completa: solo Club Pro (o administrador).
  const { hasPanel, isAdmin, loading: planLoading } = useHasActivePanel();
  const hasClubPro = usePlanGate("tool.guias", hasPanel || isAdmin).allowed;

  // Fetch article — matches against slug_es OR slug_en OR legacy slug
  useEffect(() => {
    if (!slug) return;
    const fetchArticle = async () => {
      const column = isEnglishUrl ? "slug_en" : "slug_es";
      // 1) Try canonical column for this language
      let { data } = await supabase
        .from("blog_articles")
        .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, content_html_es, content_html_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id")
        .eq(column, slug)
        .eq("status", "published")
        .maybeSingle();

      // 2) Fallback to legacy `slug` column (old bookmarks)
      if (!data) {
        const fallback = await supabase
          .from("blog_articles")
          .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, content_html_es, content_html_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id")
          .eq("slug", slug)
          .eq("status", "published")
          .maybeSingle();
        data = fallback.data;
      }

      if (!data) {
        // Premium article that current user is not entitled to: fall back to public view
        // so the paywall UI can render with metadata (no content_html).
        const viewCol = isEnglishUrl ? "slug_en" : "slug_es";
        let { data: meta } = await supabase
          .from("blog_articles_public")
          .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id")
          .eq(viewCol, slug)
          .maybeSingle();
        if (!meta) {
          const fb = await supabase
            .from("blog_articles_public")
            .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, keywords, seo_title_es, seo_title_en, seo_description_es, seo_description_en, published_at, updated_at, category_id")
            .eq("slug", slug)
            .maybeSingle();
          meta = fb.data;
        }
        if (!meta) {
          navigate(isEnglishUrl ? "/en/insights" : "/insights", { replace: true });
          return;
        }
        // Sin acceso completo servimos el arranque real del artículo (≈1/3)
        // mediante RPC, para que cualquiera pueda empezar a leerlo.
        const { data: teaser } = await supabase.rpc("get_article_teaser", {
          _slug: slug,
          _lang: isEnglishUrl ? "en" : "es",
        });
        const teaserHtml = Array.isArray(teaser) ? (teaser[0] as any)?.teaser_html ?? "" : "";
        data = {
          ...(meta as any),
          content_html_es: isEnglishUrl ? "" : teaserHtml,
          content_html_en: isEnglishUrl ? teaserHtml : "",
        } as any;
      }

      const { data: assets } = await supabase.rpc("get_article_premium_assets", { _article_id: (data as any).id });
      setArticle({ ...(data as any), premium_assets: Array.isArray(assets) ? assets : [] } as FullArticle);

      if ((data as any).category_id) {
        const { data: catData } = await supabase
          .from("blog_categories")
          .select("*")
          .eq("id", (data as any).category_id)
          .single();
        setCategory(catData as Category | null);
      }
      setLoading(false);
    };
    fetchArticle();
  }, [slug, navigate, isEnglishUrl]);

  // Per-page localized override so the language switcher in the Header
  // navigates to the translated slug instead of the static map.
  const isGuide = article?.article_type === "guide" || isGuideUrl;
  const basePathEs = isGuide ? "/guias" : "/insights";
  const basePathEn = isGuide ? "/en/guides" : "/en/insights";
  const override = useMemo(() => {
    if (!article) return null;
    return {
      pathEs: `${basePathEs}/${article.slug_es || article.slug}`,
      pathEn: `${basePathEn}/${article.slug_en || article.slug}`,
    };
  }, [article, basePathEs, basePathEn]);

  const { lang } = useLocalizedRoute(override);

  const isClubPro = article?.article_type === "guide" || article?.article_type === "premium";
  const title = article ? (lang === "es" ? article.title_es : article.title_en) : "";
  const rawContent = article ? (lang === "es" ? article.content_html_es : article.content_html_en) : "";
  const contentHtml = useMemo(() => injectHeadingIds(rawContent), [rawContent]);
  const readingTime = useMemo(() => estimateReadingTime(rawContent), [rawContent]);

  const seoTitle = article
    ? (lang === "es" ? article.seo_title_es || article.title_es : article.seo_title_en || article.title_en)
    : "";
  const seoDesc = article
    ? (lang === "es" ? article.seo_description_es || article.excerpt_es : article.seo_description_en || article.excerpt_en)
    : "";
  const categoryName = category ? (lang === "es" ? category.name_es : category.name_en) : null;

  const pathEs = override?.pathEs ?? `${basePathEs}/${slug}`;
  const pathEn = override?.pathEn ?? `${basePathEn}/${slug}`;
  const currentPath = lang === "es" ? pathEs : pathEn;
  const articleUrl = `https://retaillemon.com${currentPath}`;

  const articleJsonLd: Record<string, unknown> | undefined = article
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title,
        description: seoDesc,
        datePublished: article.published_at,
        dateModified: article.updated_at || article.published_at,
        url: articleUrl,
        mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".article-content p"] },
        author: { "@type": "Organization", name: AUTHOR_NAME, url: AUTHOR_URL },
        publisher: {
          "@type": "Organization",
          name: "Retail Lemon",
          url: "https://retaillemon.com",
          logo: {
            "@type": "ImageObject",
            url: "https://retaillemon.com/lovable-uploads/d7c0912e-86e6-4d29-ad39-e54f7b52365a.png",
          },
        },
        ...(article.cover_image_url
          ? {
              image: {
                "@type": "ImageObject",
                url: article.cover_image_url,
                caption:
                  (lang === "es" ? article.featured_image_alt_es : article.featured_image_alt_en) || title,
                creditText: "Retail Lemon",
                creator: {
                  "@type": "Organization",
                  name: "Retail Lemon",
                  url: "https://retaillemon.com",
                },
                copyrightHolder: {
                  "@type": "Organization",
                  name: "Retail Lemon",
                  url: "https://retaillemon.com",
                },
                copyrightNotice:
                  lang === "es"
                    ? "© 2026 Retail Lemon. Todos los derechos reservados."
                    : "© 2026 Retail Lemon. All rights reserved.",
                license:
                  lang === "es"
                    ? "https://retaillemon.com/condiciones-contratacion"
                    : "https://retaillemon.com/en/terms",
                acquireLicensePage: "https://retaillemon.com/contacto",
              },
            }
          : {}),

        wordCount: rawContent.replace(/<[^>]+>/g, " ").split(/\s+/).length,
        articleSection: categoryName || "Retail",
        inLanguage: lang === "es" ? "es" : "en",
      }
    : undefined;

  const renderPremiumAssets = () => {
    if (!article) return null;
    const premiumAssets = Array.isArray(article.premium_assets) ? article.premium_assets : [];
    if (premiumAssets.length === 0) return null;
    return (
      <aside className="mt-8 space-y-4">
        {premiumAssets.map((asset: any, i: number) => (
          <PremiumAssetBox key={i} asset={asset} hasAccess={false} onUpgrade={() => navigate("/#precios")} />
        ))}
      </aside>
    );
  };

  const renderContent = () => (
    <>
      <ArticleContentRenderer contentHtml={contentHtml} />
      {renderPremiumAssets()}
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container max-w-3xl mx-auto px-4 pt-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </main>
      </div>
    );
  }

  if (!article) return null;

  const publishDate = new Date(article.published_at);
  const insightsListPath = lang === "es" ? "/insights" : "/en/insights";

  return (
    <div className="min-h-screen bg-background font-body">
      <SEO
        title={seoTitle ? `${seoTitle} — Retail Lemon` : "Retail Lemon"}
        description={seoDesc}
        path={currentPath}
        pathEs={pathEs}
        pathEn={pathEn}
        lang={lang}
        type="article"
        image={article?.cover_image_url || undefined}
        imageAlt={title}
        jsonLd={articleJsonLd}
        {...(isCorporateArticle({
          titleEs: article.title_es,
          titleEn: article.title_en,
          categorySlug: category?.slug ?? null,
        })
          ? { robots: "noindex, follow" }
          : {})}
      />
      <Header />

      <SEOBreadcrumb
        items={[
          { label: lang === "es" ? "Inicio" : "Home", href: lang === "es" ? "/" : "/en" },
          { label: "Insights", href: insightsListPath },
          ...(category
            ? [{ label: categoryName || "", href: `${insightsListPath}?cat=${category.slug}` }]
            : []),
          { label: title },
        ]}
      />

      <main className="pt-4 pb-20">
        <section className="container max-w-3xl mx-auto px-4">
          <Link
            to={insightsListPath}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === "es" ? "Volver a Insights" : "Back to Insights"}
          </Link>

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Badge
                    className="text-[11px] font-bold tracking-wide uppercase border-0 text-white"
                    style={{ backgroundColor: category.color }}
                  >
                    {categoryName}
                  </Badge>
                )}
                {isClubPro && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] font-bold border-0">
                    {isGuide
                      ? lang === "es" ? "Guía Club Pro" : "Club Pro Guide"
                      : lang === "es" ? "Artículo Club Pro" : "Club Pro Article"}
                  </Badge>
                )}
              </div>

              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-[1.1] mb-5">
                {title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4" />
                  <time dateTime={article.published_at}>
                    {publishDate.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </time>
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {lang === "es" ? `${readingTime} min de lectura` : `${readingTime} min read`}
                </span>
                <span className="flex items-center gap-1.5">{AUTHOR_NAME}</span>
              </div>
            </header>

            {article.cover_image_url && (
              <figure className="mb-10">
                <img
                  src={article.cover_image_url}
                  alt={
                    (lang === "es" ? article.featured_image_alt_es : article.featured_image_alt_en) ||
                    article.cover_image_alt ||
                    `${title} | Retail Lemon`
                  }
                  title={article.featured_image_title || `Retail Lemon · ${title}`}
                  className="w-full rounded-2xl shadow-lg aspect-[2/1] object-cover"
                  loading="eager"
                  fetchPriority="high"
                  width={960}
                  height={480}
                 decoding="async" />
              </figure>
            )}

            <ArticleTableOfContents contentHtml={rawContent} />

            <section className="mt-8">
              {isClubPro ? (
                <GatedContent hasClubPro={hasClubPro}>{renderContent()}</GatedContent>
              ) : (
                renderContent()
              )}
            </section>
          </article>

          {!planLoading && !hasClubPro && <ArticleCaseCta />}

          <RelatedArticles currentArticleId={article.id} categoryId={article.category_id} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InsightArticle;
